import { useState, useEffect, useMemo, useCallback } from "react";
import * as Icon from "react-feather";
import { Link, useParams } from "react-router";
import { useSelector } from "react-redux";

import APIManager from "../../managers/APIManager";
import HelperVideo from "../../helpers/video";
import ClipboardButton from "../common/ClipboardButton";
import Button from "../common/Button";
import Card from "../common/Card";
import Form from "../common/Form";
import Page from "../common/Page";
import Loading from "../common/Loading";

import { Translation } from "react-i18next";
import { capitalizeFirst } from "../../utils/manipulateText";
import VideoList from "./VideoList";

const VideoEmbed = () => {
  const params = useParams();
  const videoId = useMemo(() => params.video, [params.video]);

  const videoInfrastructure = useSelector(
    (state) => state?.auth?.user?.videoInfrastructure
  );

  const integrationType = useSelector(
    (state) => state?.auth?.user?.integrationType
  );

  const [embedCode, setEmbedCode] = useState("");
  const [options, setOptions] = useState({
    autoplay: true,
    player: "",
    sticky: true,
  });
  const [player, setPlayer] = useState(null);
  const [players, setPlayers] = useState([]);
  const [video, setVideo] = useState(null);

  const updateEmbedCode = useCallback(
    (optionsArg) => {
      optionsArg = Object.assign({}, optionsArg || options);
      let embedCodeTmp = `<script async src="https://cdn.empower.net/sdk/EmpowerSDK.min.js"></script>
<div class="empower-player" 
\tdata-empower-id="${videoId}"`;
      if (optionsArg.player && optionsArg.player.length) {
        embedCodeTmp += `\n\tdata-empower-player="${optionsArg.player}"`;
      }
      if (!optionsArg.autoplay || !optionsArg.sticky) {
        embedCodeTmp += '\n\tdata-empower-optionsArg="';
        Object.keys(optionsArg).map(
          (option) => optionsArg[option] && delete optionsArg[option]
        );
        embedCodeTmp += JSON.stringify(optionsArg);
        embedCodeTmp += '"';
      }
      embedCodeTmp += ">\n</div>";
      setEmbedCode(embedCodeTmp);
    },
    [options, videoId]
  );

  useEffect(() => {
    APIManager.getVideo(videoId).then((res) => {
      if (res.data.status === "AVAILABLE") {
        updateEmbedCode();
      }
      if (res.data.id.startsWith("nkt:")) {
        res.data.source = `https://api.empower.net/v3/embed/npm_empowernet/${res.data.id.substring(
          4
        )}`;
      }

      setVideo(res.data);
    });
    APIManager.getPlayers().then((res) => {
      setOptions((options) => {
        res.data.forEach((player) => {
          if (player.isDefault) {
            options.player = player.id;
          }
        });
        return options;
      });
      setPlayers(res.data);

      // setState({ options, players }, updateEmbedCode());
    });
  }, [updateEmbedCode, videoId]);

  // the callback function to setState
  useEffect(() => {
    updateEmbedCode();
  }, [updateEmbedCode, players]);

  useEffect(() => {
    if (
      !video ||
      player ||
      video.status !== "AVAILABLE" ||
      video.id.startsWith("nkt:")
    ) {
      return;
    }
    const playerVar = new window.EmpowerPlayer("preview", {
      adTimeout: 1,
      language: "en",
      logo: { image: "" },
      playerName: "empower_console",
    });
    playerVar.setInfoLoadURL(
      `${process.env.REACT_APP_API_BASE}/videos/{VIDEO_ID}/playback`
    );
    playerVar.loadVideo(videoId);
    setPlayer(playerVar);
  }, [video, player, videoId]);

  const onChange = (e) => {
    const optionsVar = options;
    if (e.target.name === "player") {
      optionsVar[e.target.name] = e.target.value;
    } else {
      optionsVar[e.target.name] = !optionsVar[e.target.name];
    }
    setOptions(optionsVar);
    updateEmbedCode(optionsVar);
  };

  // TODO: is this correct? ask denizcan
  if (integrationType === -1 || videoInfrastructure === "IZLESENE") {
    return <VideoList />;
  }
  if (video === null) {
    return <Loading />;
  }

  return (
    <Page
      title={video.title}
      controls={
        <>
          <Button
            className="mr-1"
            RootComponent={Link}
            to={`/analytics/videos/${video.id}`}
            color="primary"
            icon="trending-up"
          >
            <Icon.BarChart2
              className="inline mr-1 text-empower-500"
              width="16"
              height="16"
            />
            Analytics
          </Button>
          <Button
            RootComponent={Link}
            to={`/videos/${video.id}`}
            color="primary"
            icon="edit-2"
          >
            <Icon.Edit2
              className="inline mr-1 text-empower-500"
              width="16"
              height="16"
            />
            Edit Video
          </Button>
        </>
      }
    >
      {HelperVideo.getVideoStatusAlert(video)}
      <div className="flex flex-col md:flex-row">
        <div className="flex-1 mr-5">
          <Card title="embed code">
            <Card.Body>
              <p className="pb-3">
                <Translation>
                  {(t) =>
                    capitalizeFirst(
                      t(
                        "place the following embed code on your website where you wish the player to be displayed."
                      )
                    )
                  }
                </Translation>
              </p>
              <p className="pb-3">
                You may alter the default behavior of the player using by
                toggling the options below. For more advanced usage, please
                refer to the documentation by clicking{" "}
                <a
                  href="https://developers.empower.net/"
                  className="text-blue-700"
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  here
                </a>
                .
              </p>
              <code className="code-block bg-gray-200 text-xs break-words">
                {embedCode}
              </code>
              <Form.Select
                className="w-full"
                name="player"
                label="Player"
                onChange={onChange}
              >
                {players.map((player, index) => (
                  <option
                    key={index}
                    selected={player.isDefault}
                    value={player.id}
                  >
                    {player.name +
                      (player.isDefault ? " (Default Player)" : "")}
                  </option>
                ))}
              </Form.Select>
              <Form.Label>options</Form.Label>
              <div className="flex">
                <div className="flex-1 mr-5">
                  <Form.FormCheckboxInput
                    name="autoplay"
                    label="autoplay"
                    onChange={onChange}
                    checked={options.autoplay}
                  />
                </div>
                <div className="flex-1 mr-5">
                  <Form.FormCheckboxInput
                    name="sticky"
                    label="sticky"
                    onChange={onChange}
                    checked={options.sticky}
                  />
                </div>
              </div>
            </Card.Body>
            <Card.Footer>
              <ClipboardButton content={embedCode} />
            </Card.Footer>
          </Card>
        </div>
        <div className="w-auto">
          <Card>
            {video.id.startsWith("nkt:") ? (
              <iframe
                title="Video Player"
                src={video.source}
                width="498"
                height="280"
                allow="autoplay; fullscreen"
                allowFullScreen
              />
            ) : (
              <div
                id="preview"
                className="empower-player"
                data-empower-id={video.id}
                data-empower-options='{"language":"en"}'
                style={{
                  backgroundColor: "#000",
                  height: 280,
                  width: 498,
                }}
              />
            )}
          </Card>
        </div>
      </div>
    </Page>
  );
};

export default VideoEmbed;
